import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const QCContext = createContext();

// Map GETGRIDGATE roles to QC roles
const mapRole = (getgridgateRoles) => {
  if (!getgridgateRoles || getgridgateRoles.length === 0) return 'employee';
  if (getgridgateRoles.includes('ADMIN')) return 'admin';
  if (getgridgateRoles.includes('SUPERVISOR')) return 'supervisor';
  if (getgridgateRoles.includes('CREW_LEADER')) return 'supervisor';
  return 'employee';
};

// Map GETGRIDGATE user to QC user format
const mapUser = (getgridgateUser, profile) => {
  if (!getgridgateUser) return null;
  return {
    id: getgridgateUser.id,
    email: getgridgateUser.email,
    name: getgridgateUser.name,
    role: mapRole(getgridgateUser.roles),
    employee_class: profile?.employee_class || 'Helper',
    department: getgridgateUser.department || 'general',
    overtime_hours: profile?.overtime_hours || 0,
    ranking: profile?.ranking || 1,
    class_priority: profile?.class_priority || 4,
    status: profile?.status || 'available',
    daily_ot_preference: profile?.daily_ot_preference || 'AO',
    availability: profile?.availability || 'AO',
    availability_label: profile?.availability_label || 'AO',
  };
};

export const QCProvider = ({ children }) => {
  const { user: getgridgateUser, api, logout } = useAuth();
  const [qcUser, setQcUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [theme, setTheme] = useState('light');

  // Fetch user profile for overtime/class info
  const fetchProfile = useCallback(async () => {
    if (!getgridgateUser) {
      setQcUser(null);
      setLoading(false);
      return;
    }
    
    try {
      // Try to get profile data
      const profileRes = await api.get('/my/profile').catch(() => ({ data: null }));
      setProfile(profileRes.data);
      setQcUser(mapUser(getgridgateUser, profileRes.data));
    } catch (error) {
      console.error('Error fetching profile:', error);
      setQcUser(mapUser(getgridgateUser, null));
    } finally {
      setLoading(false);
    }
  }, [getgridgateUser, api]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const toggleEmergencyMode = () => {
    setEmergencyMode(!emergencyMode);
  };

  const value = {
    user: qcUser,
    loading,
    api,
    logout,
    theme,
    toggleTheme,
    emergencyMode,
    toggleEmergencyMode,
    refreshProfile: fetchProfile,
  };

  return (
    <QCContext.Provider value={value}>
      {children}
    </QCContext.Provider>
  );
};

export const useQC = () => {
  const context = useContext(QCContext);
  if (!context) {
    throw new Error('useQC must be used within a QCProvider');
  }
  return context;
};

export default QCContext;
